import { useEffect, useState } from "react";
import { NavLink, Outlet, useNavigate, useLocation } from "react-router-dom";
import { Home, Archive, Plus, Bell, User, Mic, Camera, PenLine, FileText } from "lucide-react";
import { Drawer, DrawerContent, DrawerTitle, DrawerDescription } from "@/components/ui/drawer";
import { playTap } from "@/lib/sounds";

export const CAPTURE_MODES = [
  { key: "voz", label: "Falar", icon: Mic, bg: "#4E78FF", fg: "#fff", hint: "Guarde por voz, de forma natural." },
  { key: "camera", label: "Câmera", icon: Camera, bg: "#13C9A3", fg: "#fff", hint: "Foto ou vídeo de objetos e lugares." },
  { key: "texto", label: "Escrever", icon: PenLine, bg: "#7657E8", fg: "#fff", hint: "Escreva em poucas palavras." },
  { key: "documento", label: "Documento", icon: FileText, bg: "var(--ga-surface)", fg: "var(--ga-text)", hint: "Notas, garantias e PDFs." },
];

export function CaptureMenu({ open, onOpenChange }) {
  const navigate = useNavigate();
  return (
    <Drawer open={open} onOpenChange={onOpenChange}>
      <DrawerContent className="mx-auto max-w-md rounded-t-3xl border-0 px-5 pb-8" style={{ background: "var(--ga-card)" }} data-testid="capture-menu">
        <DrawerTitle className="pt-2 text-lg font-semibold">O que você quer guardar?</DrawerTitle>
        <DrawerDescription className="ga-muted text-sm">Registre em segundos.</DrawerDescription>
        <div className="mt-5 grid grid-cols-2 gap-3">
          {CAPTURE_MODES.map((m) => (
            <button
              key={m.key}
              data-testid={`capture-menu-${m.key}`}
              onClick={() => { playTap(); onOpenChange(false); navigate(`/nova/${m.key}`); }}
              className="flex flex-col items-start gap-3 rounded-2xl p-4 text-left transition-transform active:scale-[0.97]"
              style={{ background: m.bg, color: m.fg }}
            >
              <m.icon size={24} />
              <div>
                <div className="text-base font-semibold">{m.label}</div>
                <div className="text-xs opacity-80">{m.hint}</div>
              </div>
            </button>
          ))}
        </div>
      </DrawerContent>
    </Drawer>
  );
}

const items = [
  { to: "/inicio", label: "Início", icon: Home, id: "inicio" },
  { to: "/memorias", label: "Memórias", icon: Archive, id: "memorias" },
  null,
  { to: "/lembretes", label: "Lembretes", icon: Bell, id: "lembretes" },
  { to: "/perfil", label: "Perfil", icon: User, id: "perfil" },
];

export default function AppShell() {
  const [menuOpen, setMenuOpen] = useState(false);
  const { pathname } = useLocation();
  useEffect(() => { setMenuOpen(false); }, [pathname]);
  return (
    <div className="relative mx-auto flex min-h-screen w-full max-w-md flex-col pb-40" style={{ background: "var(--ga-bg)" }}>
      <main className="flex-1">
        <Outlet />
      </main>
      <nav className="fixed bottom-0 left-1/2 z-40 w-full max-w-md -translate-x-1/2 border-t px-2 pb-[max(env(safe-area-inset-bottom),10px)] pt-2" style={{ background: "var(--ga-card)", borderColor: "var(--ga-border)" }} aria-label="Navegação principal" data-testid="bottom-nav">
        <div className="grid grid-cols-5 items-end">
          {items.map((it, i) =>
            it ? (
              <NavLink key={it.id} to={it.to} onClick={playTap} data-testid={`bottom-nav-${it.id}`} className="flex flex-col items-center gap-1 py-1 text-[11px] font-medium transition-colors" style={({ isActive }) => ({ color: isActive ? "#13C9A3" : "var(--ga-muted)" })}>
                <it.icon size={22} strokeWidth={2.2} />
                {it.label}
              </NavLink>
            ) : (
              <div key="plus" className="flex justify-center">
                <button
                  data-testid="bottom-nav-novo"
                  aria-label="Nova memória"
                  onClick={() => { playTap(); setMenuOpen(true); }}
                  className="-mt-7 flex h-16 w-16 items-center justify-center rounded-full text-white transition-transform hover:scale-105 active:scale-95"
                  style={{ background: "#13C9A3", boxShadow: "0 10px 24px -6px rgba(19,201,163,.7)", border: "4px solid var(--ga-card)" }}
                >
                  <Plus size={30} strokeWidth={2.5} />
                </button>
              </div>
            )
          )}
        </div>
      </nav>
      <CaptureMenu open={menuOpen} onOpenChange={setMenuOpen} />
    </div>
  );
}
